import camelcaseKeys from "camelcase-keys";
import snakecaseKeys from "snakecase-keys";

import { Attempt } from "./hooks/useAttempts";
import config from "./config";
import {
  ApiCompetitionEntry,
  ApiMedia,
  ApiProfile,
  ApiJudgingGroup,
  ApiCompetition,
} from "./types";
import getApiErrorHasDetail from "./utils/getApiErrorHasDetail";

const camelCaseExcludeRegex = [
  /\b([A-Z0-9_]+)\b|-1|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/,
];

const CLIENT_ID = config.CLIENT_ID;
const CLIENT_SECRET = config.CLIENT_SECRET;
const BASE_URL = config.API_BASE_URL;

export class ApiError extends Error {
  info: unknown;
  status?: number;
}

export type ApiErrorDetailItem = { message: string; code: string };

export type ApiErrorWithDetail = ApiError & {
  info: {
    detail: {
      non_field_errors?: ApiErrorDetailItem[];
    } & Record<string, ApiErrorDetailItem[]>;
  };
};

type AuthOptions<T = unknown> = {
  accessToken?: string;
  refreshToken?: string;
  onAuthExpire: (retry: (auth: AuthOptions<T>) => Promise<T>) => Promise<T>;
};

type FetchOpts<T> = RequestInit & {
  autoUnpaginate?: boolean;
  auth?: AuthOptions<T>;
  jsonData?: unknown[] | Record<string, unknown>;
};

export const apiFetch = async <T = unknown>(
  url: string,
  opts?: FetchOpts<T>
): Promise<T> => {
  const auth = opts?.auth;

  const autoUnpaginate = opts?.autoUnpaginate ?? true;

  const res = await fetch(BASE_URL + url, {
    ...opts,
    body:
      opts?.body ??
      (opts?.jsonData !== undefined
        ? JSON.stringify(snakecaseKeys(opts.jsonData))
        : undefined),
    headers: {
      ...(auth?.accessToken
        ? { Authorization: `Bearer ${auth?.accessToken}` }
        : {}),
      "Content-Type": "application/json",
      ...(opts?.headers || {}),
    },
  });

  if (!res.ok) {
    if (res.status === 401 && auth?.refreshToken && auth.onAuthExpire) {
      const retry = (auth_: AuthOptions<T>) =>
        apiFetch(url, { ...opts, auth: auth_ });
      try {
        return await auth.onAuthExpire(retry);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }

    const error = new ApiError("An error occurred while fetching the data.");

    // Attach extra info to the error object.
    try {
      error.info = await res.json();
    } catch (jsonError) {
      error.info = undefined;
    }
    error.status = res.status;
    throw error;
  }

  if (res.status === 204) {
    return undefined as unknown as T;
  }

  let result = camelcaseKeys(await res.json(), {
    exclude: camelCaseExcludeRegex,
    deep: true,
  });
  if (
    autoUnpaginate &&
    typeof result === "object" &&
    "data" in result &&
    "metadata" in result
  ) {
    const allData = [result.data];
    while (result.metadata.next) {
      result = await apiFetch(result.metadata.next, {
        ...opts,
        autoUnpaginate: false,
      });
      allData.push(result.data);
    }
    return allData.flat() as unknown as T;
  } else {
    return result;
  }
};

type ClearAttemptsPayload = { routeUUID: string; userUUID: string };

export const clearAttempts = ({
  routeUUID,
  userUUID,
}: ClearAttemptsPayload) => {
  return apiFetchWithAuth(`/routes/${routeUUID}/attempts?user=${userUUID}`, {
    method: "DELETE",
  });
};

type UpdateAttemptsPayload = {
  routeUUID: string;
  userUUID: string;
  attempts: Attempt[];
};

export const updateAttempts = ({
  routeUUID,
  userUUID,
  attempts,
}: UpdateAttemptsPayload) => {
  return apiFetchWithAuth(`/routes/${routeUUID}/attempts?user=${userUUID}`, {
    method: "PATCH",
    jsonData: attempts,
  });
};

export const register = async ({
  email,
  password,
  firstName,
  lastName,
  yearOfBirth,
  gender,
  profilePicture,
}: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  gender?: string;
  yearOfBirth?: number;
  profilePicture?: File;
}) => {
  try {
    const { accessToken, refreshToken } = await apiFetch<{
      accessToken: string;
      refreshToken: string;
    }>(`/auth/register`, {
      method: "POST",
      body: JSON.stringify({ email, password }),
      headers: {
        Authorization: "Basic " + window.btoa(`${CLIENT_ID}:${CLIENT_SECRET}`),
      },
    });
    sessionStorage.setItem("accessToken", accessToken);
    sessionStorage.setItem("accessTokenUpdated", new Date().toISOString());
    sessionStorage.setItem("refreshToken", refreshToken);
  } catch (error) {
    if (
      getApiErrorHasDetail(error) &&
      error.info.detail.non_field_errors?.[0]?.message.includes(
        "already exists"
      )
    ) {
      // Try to log in here if user already exists
      await login({ email, password });
    } else {
      throw error;
    }
  }
  if (profilePicture instanceof File) {
    await setProfilePicture(profilePicture);
  }
  return await apiFetchWithAuth<ApiProfile>("/profiles/me", {
    method: "PATCH",
    jsonData: {
      firstName,
      lastName,
      yearOfBirth,
      gender,
    },
  });
};

export const registerChild = async ({
  firstName,
  lastName,
  profilePicture,
  yearOfBirth,
  gender,
}: {
  firstName: string;
  lastName: string;
  profilePicture?: File;
  yearOfBirth?: number;
  gender?: string;
}) => {
  const profile = await apiFetchWithAuth<ApiProfile>("/profiles/me/children", {
    method: "POST",
    jsonData: {
      firstName,
      lastName,
      yearOfBirth,
      gender,
    },
  });
  if (profilePicture) {
    const media = await setProfilePicture(profilePicture, profile.uuid);
    profile.picture = media.uuid;
    profile.pictureUrl = media.url;
  }
  return profile;
};

export const patchProfile = async ({
  firstName,
  lastName,
  profilePicture,
  yearOfBirth,
  gender,
  uuid = "me",
}: {
  firstName: string;
  lastName: string;
  profilePicture?: { uuid: string } | File;
  yearOfBirth?: number;
  gender?: string;
  uuid?: string;
}) => {
  if (profilePicture instanceof File) {
    await setProfilePicture(profilePicture);
  }
  return await apiFetchWithAuth<ApiProfile>(`/profiles/${uuid}`, {
    method: "PATCH",
    jsonData: {
      firstName,
      lastName,
      yearOfBirth,
      gender,
      ...(profilePicture && !(profilePicture instanceof File)
        ? {
            picture: profilePicture.uuid,
            pictureSource: "MEDIA",
          }
        : {}),
    },
  });
};

export const patchEntry = async ({
  competitionUUID,
  userUUID,
  categoryUUID,
}: {
  competitionUUID?: string;
  userUUID?: string;
  categoryUUID?: string;
}) => {
  return await apiFetchWithAuth<ApiProfile>(
    `/competitions/${competitionUUID}/entries/${userUUID}`,
    {
      method: "PATCH",
      jsonData: {
        category: categoryUUID,
      },
    }
  );
};

export const setProfilePicture = async (file: File, uuid = "me") => {
  const media = await uploadMedia(file);
  await apiFetchWithAuth(`/profiles/${uuid}`, {
    method: "PATCH",
    body: JSON.stringify({ picture: media.uuid, picture_source: "MEDIA" }),
  });
  return media;
};

export const checkEmailExists = (email: string) =>
  apiFetchWithAuth<boolean>(`/auth/check-email`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });

export const getToken = ({
  email,
  password,
}: {
  email: string;
  password: string;
}) =>
  apiFetch<{ accessToken: string; refreshToken: string }>("/auth/token", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: "Basic " + window.btoa(`${CLIENT_ID}:${CLIENT_SECRET}`),
    },
    body: new URLSearchParams({
      username: email,
      password,
      grant_type: "password",
    }),
  });

export const refreshAccessToken = (refreshToken: string) =>
  apiFetch<{
    accessToken: string;
    refreshToken: string;
  }>("/auth/token", {
    method: "POST",
    headers: {
      Authorization: "Basic " + window.btoa(`${CLIENT_ID}:${CLIENT_SECRET}`),
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    }),
  });

type RetryFn<T> = (auth: AuthOptions<T>) => Promise<T>;

export const apiFetchWithAuth = async <T = unknown>(
  url: string,
  opts: Omit<FetchOpts<T>, "auth"> = {}
) => {
  const tokenUpdateDateStr = sessionStorage.getItem("accessTokenUpdated");
  if (
    !sessionStorage.getItem("refreshToken") &&
    (!tokenUpdateDateStr ||
      new Date().getTime() - new Date(tokenUpdateDateStr).getTime() > 36000000)
  ) {
    await saveClientCredentials();
  }
  const auth = {
    accessToken: sessionStorage.getItem("accessToken") || undefined,
    refreshToken: sessionStorage.getItem("refreshToken") || undefined,
    onAuthExpire: async (retry: RetryFn<T>) => {
      if (url !== "/auth/logout") {
        if (auth.refreshToken) {
          try {
            const { accessToken, refreshToken } = await refreshAccessToken(
              auth.refreshToken
            );
            const newAuth = { ...auth, accessToken, refreshToken };
            sessionStorage.setItem("refreshToken", refreshToken);
            sessionStorage.setItem("accessToken", accessToken);
            sessionStorage.setItem(
              "accessTokenUpdated",
              new Date().toISOString()
            );
            return retry(newAuth);
          } catch (error) {
            // TODO (future PR): setup sentry
            // eslint-disable-next-line no-console
            console.error(error);
            sessionStorage.removeItem("refreshToken");
            sessionStorage.removeItem("accessToken");
            const { accessToken } = await saveClientCredentials();
            return retry({ ...auth, accessToken, refreshToken: undefined });
          }
        } else {
          const { accessToken } = await saveClientCredentials();
          return retry({ ...auth, refreshToken: undefined, accessToken });
        }
      }
      throw new Error("Not retrying as was logout");
    },
  };
  return await apiFetch(url, { ...opts, auth });
};

export const login = async ({
  email,
  password,
}: {
  email: string;
  password: string;
}) => {
  const res = await getToken({ email, password });
  sessionStorage.setItem("accessToken", res.accessToken);
  sessionStorage.setItem("accessTokenUpdated", new Date().toISOString());
  sessionStorage.setItem("refreshToken", res.refreshToken);
};

/**
 * Exchanges a single use auth code for an access token.
 */
export const exchangeSingleUseAuthCode = async (
  code: string,
  redirectUri?: string
) => {
  const res: { accessToken: string; refreshToken: string } = await apiFetch(
    "/auth/token",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        code,
        redirect_uri: redirectUri || window.location.toString(),
        client_id: CLIENT_ID,
      }),
    }
  );
  sessionStorage.setItem("accessToken", res.accessToken);
  sessionStorage.setItem("accessTokenUpdated", new Date().toISOString());
  sessionStorage.setItem("refreshToken", res.refreshToken);
};

export const saveClientCredentials = async () => {
  const res = await apiFetch<{ accessToken: string }>("/auth/token", {
    method: "POST",
    headers: {
      Authorization: "Basic " + window.btoa(`${CLIENT_ID}:${CLIENT_SECRET}`),
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({ grant_type: "client_credentials" }),
  });
  sessionStorage.setItem("accessToken", res.accessToken);
  sessionStorage.setItem("accessTokenUpdated", new Date().toISOString());
  return res;
};

/**
 * Upload a media file to the server. Mainly used for profile pictures.
 */
export const uploadMedia = async (file: File) => {
  const media = await apiFetchWithAuth<ApiMedia>("/media", {
    method: "POST",
    jsonData: {
      type: file.type,
      captureTime: new Date(file.lastModified).toISOString(),
    },
  });
  if (!media.signedUploadUrl) {
    throw new Error("This should never happen");
  }
  const uploadResult = await fetch(media.signedUploadUrl, {
    method: "PUT",
    body: file,
  });
  if (uploadResult.ok) {
    return media;
  } else {
    const error = new ApiError("An error occurred while uploading.");
    // Attach extra info to the error object.
    error.info = await uploadResult.json();
    error.status = uploadResult.status;
    throw error;
  }
};

type EnterCompParams = {
  userUUID: string;
  competitionUUID: string;
  categoryUUID: string;
  competitorNumber?: string;
};

/**
 * Enter the competition with the given user and category.
 */
export const enterCompetition = async ({
  userUUID,
  competitionUUID,
  categoryUUID,
  competitorNumber,
}: EnterCompParams) => {
  try {
    await apiFetchWithAuth<ApiCompetitionEntry>(
      `/competitions/${competitionUUID}/entries`,
      {
        method: "POST",
        jsonData: {
          category: categoryUUID,
          user: userUUID === "me" ? undefined : userUUID,
          competitorNumber,
        },
      }
    );
  } catch (error) {
    if (
      !(error instanceof ApiError) ||
      error.status !== 400 ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      !(error as any)?.info?.detail?.non_field_errors?.[0].message.includes(
        "already exists"
      )
    ) {
      throw error;
    }
  }
};

type EntryDetails = {
  userUUID: string;
  categoryUUID: string;
  competitiorNumber?: string;
};
export const enterAllIntoCompetition = (
  competitionUUID: string,
  entries: EntryDetails[]
) => {
  return Promise.all(
    entries.map((e) =>
      enterCompetition({
        ...e,
        competitorNumber: e.competitiorNumber || undefined,
        competitionUUID,
      })
    )
  );
};

export const judgeCompetition = ({
  competitionUUID,
  judgingKey,
}: {
  competitionUUID: string;
  judgingKey: string;
}) => {
  return apiFetchWithAuth(`/competitions/${competitionUUID}/judge`, {
    method: "POST",
    jsonData: {
      key: judgingKey,
    },
  });
};

export const checkJudgingKey = async ({
  competitionUUID,
  judgingKey,
}: {
  competitionUUID: string;
  judgingKey: string;
}) => {
  try {
    await apiFetchWithAuth(
      `/competitions/${competitionUUID}/judge/validate-key`,
      {
        method: "POST",
        jsonData: {
          key: judgingKey,
        },
      }
    );
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.status === 400) {
      return false;
    } else {
      throw error;
    }
  }
};

export const addToQueue = ({
  competitionUUID,
  routeUUID,
  userUUID,
}: {
  competitionUUID: string;
  routeUUID: string;
  userUUID: string;
}) =>
  apiFetchWithAuth<string[]>(
    `/competitions/${competitionUUID}/route-queues/${routeUUID}`,
    { method: "POST", body: JSON.stringify(userUUID) }
  );

export const putQueue = ({
  competitionUUID,
  routeUUID,
  queue,
}: {
  competitionUUID: string;
  routeUUID: string;
  queue: string[];
}) =>
  apiFetchWithAuth<string[]>(
    `/competitions/${competitionUUID}/route-queues/${routeUUID}`,
    { method: "PUT", jsonData: queue }
  );

type CompetitionUpdateRouteNumberPayload = {
  route: string;
  routeGroup: string;
  number?: number | null;
}[];

export const updateRouteNumbers = (
  competitionUUID: string,
  data: CompetitionUpdateRouteNumberPayload
) =>
  apiFetchWithAuth(`/competitions/${competitionUUID}/route-numbers`, {
    method: "PATCH",
    jsonData: data,
  });

export const deleteEntry = ({
  competitionUUID,
  entryUUID,
}: {
  competitionUUID: string;
  entryUUID: string;
}) =>
  apiFetchWithAuth(`/competitions/${competitionUUID}/entries/${entryUUID}`, {
    method: "DELETE",
  });

export const mergeEntries = ({
  competitionUUID,
  entryUUID,
  targetEntryUUID,
}: {
  competitionUUID: string;
  entryUUID: string;
  targetEntryUUID: string;
}) =>
  apiFetchWithAuth(
    `/competitions/${competitionUUID}/entries/${entryUUID}/merge`,
    {
      jsonData: { target: targetEntryUUID },
      method: "POST",
    }
  );

export const getEntryByCompetitorNumber = (
  competitionUUID: string,
  competitorNumber: string
): Promise<ApiCompetitionEntry> =>
  apiFetchWithAuth(
    `/competitions/${competitionUUID}/entries/by-competitor-number/${competitorNumber}`
  );

export const updateJudgingGroups = (
  competitionUUID: string,
  data: {
    uuid: string;
    name?: string;
    routes: string[];
  }[]
): Promise<ApiCompetition> =>
  apiFetchWithAuth(`/competitions/${competitionUUID}`, {
    method: "PATCH",
    jsonData: {
      judgingGroups: data,
    },
  });

export const createJudgingGroup = (
  competitionUUID: string,
  data: {
    name: string;
  }
): Promise<ApiJudgingGroup> =>
  apiFetchWithAuth(`/competitions/${competitionUUID}/judging-groups`, {
    method: "POST",
    jsonData: data,
  });

export const updateJudgingGroup = (
  competitionUUID: string,
  judgingGroupUUID: string,
  data: {
    name?: string;
    routes?: string[];
    judges?: string[];
  }
): Promise<ApiJudgingGroup> =>
  apiFetchWithAuth(
    `/competitions/${competitionUUID}/judging-groups/${judgingGroupUUID}`,
    {
      method: "PATCH",
      jsonData: data,
    }
  );

export const deleteJudgingGroup = (
  competitionUUID: string,
  judgingGroupUUID: string
) =>
  apiFetchWithAuth(
    `/competitions/${competitionUUID}/judging-groups/${judgingGroupUUID}`,
    {
      method: "DELETE",
    }
  );
